import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import MarkdownEditor from '../../components/MarkdownEditor'
import Logo from '../../components/Logo'

export default function Edit({ memory, projects, types, scopes }) {
  const [form, setForm] = useState({
    title:       memory.title,
    content:     memory.content,
    memory_type: memory.memory_type,
    scope:       memory.scope,
    topic_key:   memory.topic_key ?? '',
    expires_at:  memory.expires_at ? new Date(memory.expires_at).toISOString().slice(0, 16) : '',
  })

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    router.patch(`/memories/${memory.id}`, {
      title:       form.title,
      content:     form.content,
      memory_type: form.memory_type,
      scope:       form.scope,
      topic_key:   form.topic_key  || undefined,
      expires_at:  form.expires_at || undefined,
    })
  }

  return (
    <div className="min-h-screen bg-zinc-900 text-zinc-100">
      {/* Header */}
      <header className="border-b border-zinc-700 px-6 py-3 flex items-center justify-between shrink-0 bg-gradient-to-r from-zinc-900 via-zinc-900 to-zinc-800/30">
        <div className="flex items-center gap-4">
          <Link href="/" className="flex items-center gap-2.5 no-underline group">
            <Logo variant="mark" size={28} />
            <span className="text-base font-mono font-bold tracking-[0.12em] uppercase text-zinc-100 group-hover:text-emerald-300 transition-colors">
              anamnesis
            </span>
          </Link>
          <span className="text-zinc-700">|</span>
          <a
            href={`/memories/${memory.id}`}
            className="text-xs font-mono text-zinc-400 hover:text-zinc-100 transition-colors"
          >
            ← cancel
          </a>
        </div>
        <span className="text-xs font-mono text-zinc-600">editing #{memory.id}</span>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">

          {/* Title */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-mono text-zinc-500 uppercase tracking-widest">title</label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm font-mono text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600"
            />
          </div>

          {/* Type + Scope row */}
          <div className="flex gap-4">
            <div className="flex flex-col gap-1.5 flex-1">
              <label className="text-xs font-mono text-zinc-500 uppercase tracking-widest">type</label>
              <select
                name="memory_type"
                value={form.memory_type}
                onChange={handleChange}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600"
              >
                {types.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5 flex-1">
              <label className="text-xs font-mono text-zinc-500 uppercase tracking-widest">scope</label>
              <select
                name="scope"
                value={form.scope}
                onChange={handleChange}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600"
              >
                {scopes.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Topic key */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
              topic key <span className="text-zinc-700 normal-case">(optional)</span>
            </label>
            <input
              type="text"
              name="topic_key"
              value={form.topic_key}
              onChange={handleChange}
              placeholder="e.g. architecture/auth-model"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm font-mono text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600"
            />
          </div>

          {/* Expires at */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
              expires at <span className="text-zinc-700 normal-case">(UTC, optional)</span>
            </label>
            <input
              type="datetime-local"
              name="expires_at"
              value={form.expires_at}
              onChange={handleChange}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm font-mono text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600"
            />
          </div>

          {/* Content */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
              content <span className="text-zinc-700 normal-case">(markdown)</span>
            </label>
            <MarkdownEditor
              name="content"
              value={form.content}
              onChange={handleChange}
              rows={20}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              className="px-4 py-2 text-sm font-mono bg-emerald-800/60 hover:bg-emerald-700/80 text-emerald-200 border border-emerald-700/40 rounded-lg transition-colors"
            >
              save changes
            </button>
            <a
              href={`/memories/${memory.id}`}
              className="text-xs font-mono text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              cancel
            </a>
          </div>
        </form>
      </div>
    </div>
  )
}
